import React from "react";
import { Step } from "react-joyride";
import { TOUR_SELECTORS } from "./tourConfig";

export interface TourStepExtended extends Step {
  // Optional navigation requirement
  requiresNavigation?: {
    path: string;
  };
  // Optional tab switch requirement
  requiresTabSwitch?: {
    tabId: string;
  };
  // Optional feature key from subscriptionTiers — step is hidden when user's tier lacks this feature
  requiredFeature?: string;
}

export const brokerTourSteps: TourStepExtended[] = [
  // Welcome step on Dashboard
  {
    target: "body",
    content: (
      <div>
        <h2 style={{ marginBottom: "12px", fontSize: "20px", fontWeight: "600" }}>
          Welcome to Administr!
        </h2>
        <p style={{ marginBottom: "10px" }}>
          Let's take a quick tour to help you get started with your agency.
        </p>
        <p>
          We'll walk you through the main features including the navigation menu,
          agency setup, and how to manage your clients and prospects.
        </p>
      </div>
    ),
    placement: "center",
    disableBeacon: true,
  },
  
  // Header items
  {
    target: TOUR_SELECTORS.headerMessages,
    title: "Messages",
    content: (
      <div>
        <p>
          Access your synced email inbox. View and manage emails from your
          connected Gmail or Outlook account without leaving the platform.
        </p>
      </div>
    ),
    placement: "bottom",
    disableBeacon: true,
    requiredFeature: "email_sync",
  },
  {
    target: TOUR_SELECTORS.headerCalendar,
    title: "Calendar",
    content: (
      <div>
        <p>
          Schedule meetings, appointments, and events. Keep track of important
          dates and deadlines for your agency.
        </p>
      </div>
    ),
    placement: "bottom",
    disableBeacon: true,
    requiredFeature: "calendaring",
  },
  {
    target: TOUR_SELECTORS.headerTasks,
    title: "Tasks",
    content: (
      <div>
        <p>
          Manage tasks and support requests. Track client issues, assign work
          to team members, and ensure timely resolution.
        </p>
      </div>
    ),
    placement: "bottom",
    disableBeacon: true,
  },

  // Sidebar navigation items
  {
    target: TOUR_SELECTORS.sidebarDashboard,
    title: "Dashboard",
    content: (
      <div>
        <p>
          Your Dashboard provides an overview of your insurance operations,
          including key metrics, recent activity, and performance analytics.
        </p>
      </div>
    ),
    placement: "right",
    disableBeacon: true,
  },
  {
    target: TOUR_SELECTORS.sidebarAgency,
    title: "Agency Management",
    content: (
      <div>
        <p>
          This is where you'll set up and manage your agency. You can configure
          your profile, manage employees, set up roles and teams, configure
          commissions, and more.
        </p>
        <p style={{ marginTop: "10px", fontStyle: "italic", color: "#666" }}>
          We'll explore the Agency section in detail next.
        </p>
      </div>
    ),
    placement: "right",
    disableBeacon: true,
  },
  {
    target: TOUR_SELECTORS.sidebarClients,
    title: "Clients",
    content: (
      <div>
        <p>
          Manage your active clients here. View and edit client information,
          track enrollments, manage employee rosters, and monitor benefits.
        </p>
      </div>
    ),
    placement: "right",
    disableBeacon: true,
  },
  {
    target: TOUR_SELECTORS.sidebarProspects,
    title: "Prospects",
    content: (
      <div>
        <p>
          Track and manage your sales pipeline. Add new prospects, track
          conversations, manage follow-ups, and convert prospects to clients.
        </p>
      </div>
    ),
    placement: "right",
    disableBeacon: true,
  },
  {
    target: TOUR_SELECTORS.sidebarQuoting,
    title: "Quoting",
    content: (
      <div>
        <p>
          Generate and manage insurance quotes for your clients and prospects.
          Use our 9-step quote process to create accurate proposals with
          multiple carrier options.
        </p>
      </div>
    ),
    placement: "right",
    disableBeacon: true,
    requiredFeature: "instant_quote",
  },
  {
    target: TOUR_SELECTORS.sidebarMarketing,
    title: "Marketing",
    content: (
      <div>
        <p>
          Create and manage marketing campaigns. Run email and SMS campaigns
          to engage with your clients and prospects.
        </p>
      </div>
    ),
    placement: "right",
    disableBeacon: true,
    requiredFeature: "marketing_basic",
  },
  {
    target: TOUR_SELECTORS.sidebarDirectory,
    title: "Directory",
    content: (
      <div>
        <p>
          Access your agency's custom directory. Organize and access important
          contacts and resources specific to your agency.
        </p>
      </div>
    ),
    placement: "right",
    disableBeacon: true,
  },
  {
    target: TOUR_SELECTORS.sidebarReports,
    title: "Reports",
    content: (
      <div>
        <p>
          Generate comprehensive reports and analytics. Track revenue, analyze
          performance, and gain insights into your agency's operations.
        </p>
      </div>
    ),
    placement: "right",
    disableBeacon: true,
    requiredFeature: "analytics_basic",
  },
  
  // Agency page - introduction
  {
    target: "body",
    content: (
      <div>
        <h2 style={{ marginBottom: "12px", fontSize: "20px", fontWeight: "600" }}>
          Agency Setup
        </h2>
        <p>
          Now let's explore the Agency Management section where you'll configure
          your agency's profile, team, and operations.
        </p>
      </div>
    ),
    placement: "center",
    disableBeacon: true,
    requiresNavigation: {
      path: "/agency",
    },
  },
  
  // Agency tabs
  {
    target: TOUR_SELECTORS.agencyTabProfile,
    title: "Profile Tab",
    content: (
      <div>
        <p>
          Set up your agency's basic information including name, contact details,
          address, and branding.
        </p>
      </div>
    ),
    placement: "bottom",
    disableBeacon: true,
    requiresNavigation: {
      path: "/agency",
    },
  },
  {
    target: TOUR_SELECTORS.agencyTabEmployees,
    title: "Employees Tab",
    content: (
      <div>
        <p>
          Manage your agency team members. Add employees, assign roles, and
          track their information and performance.
        </p>
      </div>
    ),
    placement: "bottom",
    disableBeacon: true,
    requiresNavigation: {
      path: "/agency",
    },
  },
  {
    target: TOUR_SELECTORS.agencyTabRoles,
    title: "Roles Tab",
    content: (
      <div>
        <p>
          Define and manage user roles and permissions. Control who has access
          to different parts of the system.
        </p>
      </div>
    ),
    placement: "bottom",
    disableBeacon: true,
    requiresNavigation: {
      path: "/agency",
    },
  },
  {
    target: TOUR_SELECTORS.agencyTabTeams,
    title: "Teams Tab",
    content: (
      <div>
        <p>
          Organize your employees into teams. Create team structures that match
          your agency's organization.
        </p>
      </div>
    ),
    placement: "bottom",
    disableBeacon: true,
    requiresNavigation: {
      path: "/agency",
    },
  },
  {
    target: TOUR_SELECTORS.agencyTabCommissions,
    title: "Commissions Tab",
    content: (
      <div>
        <p>
          Configure commission structures and splits. Set up how commissions are
          calculated and distributed across your agency.
        </p>
      </div>
    ),
    placement: "bottom",
    disableBeacon: true,
    requiresNavigation: {
      path: "/agency",
    },
  },
  {
    target: TOUR_SELECTORS.agencyTabBenefits,
    title: "Benefits Tab",
    content: (
      <div>
        <p>
          Manage your agency's own benefit plans. Set up plans for your agency
          employees.
        </p>
      </div>
    ),
    placement: "bottom",
    disableBeacon: true,
    requiresNavigation: {
      path: "/agency",
    },
  },
  {
    target: TOUR_SELECTORS.agencyTabDocuments,
    title: "Documents Tab",
    content: (
      <div>
        <p>
          Store and manage important agency documents. Keep all your agency's
          paperwork organized and accessible.
        </p>
      </div>
    ),
    placement: "bottom",
    disableBeacon: true,
    requiresNavigation: {
      path: "/agency",
    },
  },
  {
    target: TOUR_SELECTORS.agencyTabOrganization,
    title: "Organization Tab",
    content: (
      <div>
        <p>
          Configure organizational settings and preferences for your agency.
        </p>
      </div>
    ),
    placement: "bottom",
    disableBeacon: true,
    requiresNavigation: {
      path: "/agency",
    },
  },
  
  // Closing step
  {
    target: "body",
    content: (
      <div>
        <h2 style={{ marginBottom: "12px", fontSize: "20px", fontWeight: "600" }}>
          You're All Set!
        </h2>
        <p style={{ marginBottom: "10px" }}>
          You've completed the tour! You now know your way around the platform.
        </p>
        <p>
          Start by setting up your agency profile, then add your clients and
          begin managing your insurance operations.
        </p>
        <p style={{ marginTop: "15px", fontSize: "13px", color: "#666" }}>
          You can restart this tour anytime from the Dashboard.
        </p>
      </div>
    ),
    placement: "center",
    disableBeacon: true,
    requiresNavigation: {
      path: "/",
    },
  },
];
